/* v-bump 2026-09-24: rotate asset version so the CDN serves the pump-merge-fix cds-3d-loader.js (its ?v= is copied from this file; ticket #83 broken impeller). */
/* Circle D — own the 3D anatomy scene's lifecycle.

   cds-3d-loader.js paints into a viewport-fixed canvas and drives its own
   opacity, but that choreography came from the previous homepage where the
   converter WAS the hero: it pins opacity to 1 for the first 1.2 viewports of
   scroll. On this homepage the machining frame is there, so the model has to
   be held back until #anatomy is actually on screen and dropped again before
   #process ("How we build it") arrives.

   Two jobs:
   1. Load the loader lazily. It pulls three.js + a 1.4MB Draco GLB, so none of
      that is fetched until the section is close.
   2. Gate visibility on the sticky stage, with explicit rect maths. An
      IntersectionObserver is the wrong tool here: #anatomy is 180vh tall and
      starts less than a viewport down the page, so it "intersects" at scroll 0
      and the model appears behind the hero. What matters is not whether the
      section is touching the viewport but whether the stage fills it.

   Visibility is deliberate — it is the one channel the loader never writes, so
   gating here can never fight its per-frame opacity. */
(function () {
  'use strict';

  /* resolve sibling assets from our own URL; both live in the theme's assets/ */
  var SELF = (document.currentScript && document.currentScript.src) || '';

  /* What the gate measures. #anatomy is the 180vh scroll runway; the thing the
     converter is actually composed against is .anatomy-sticky — a 100svh panel
     that pins to the top of the viewport and carries the component list.

     Gating on #anatomy's own rect (what this did before) is off by a whole
     screen in both directions, because the runway extends ~1.5 viewports past
     the panel in each:
       - its top edge crosses the viewport ~850px before the panel pins, so the
         converter faded up over the hero stat bar;
       - its bottom edge hits the viewport floor at the exact moment the panel
         starts to unpin, so the converter vanished while the component list was
         still sitting there, fully readable, with an empty slot beside it.
     Measuring the panel makes both edges mean what they read as. */
  var STAGE = '.anatomy-sticky';

  /* Fraction of the viewport the stage must cover to EARN the scene. Kept tight
     because anything looser puts the converter over the hero — that was the
     original bug. */
  var COVER = 0.9;

  /* ...but coverage is the wrong test for GIVING IT BACK. The stage stops
     covering the viewport the moment it unpins, while the component list it
     belongs to is still sitting there, fully readable, for most of a screen
     more — so the converter blinked out mid-read with an empty slot beside the
     list. The scene is therefore held until the following section has genuinely
     arrived: released once #process ("From Raw Billet to the Track") has taken
     the bottom 30% of the viewport. */
  var RELEASE = 0.7;

  /* The panel's hint line. The markup ships the hover-only wording because that
     is all the static fallback can offer — no canvas, nothing to scrub. The
     scrub exists only where the scene actually boots, so the promise is made
     here, by the script that knows whether the behaviour is there at all,
     rather than hardcoded in the section. Same test the loader gates on. */
  function retitleHint() {
    if (!matchMedia('(min-width: 1024px) and (pointer: fine)').matches) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var hint = document.querySelector('.anh-hint span');
    if (hint && hint.firstChild && hint.firstChild.nodeType === 3) {
      hint.firstChild.nodeValue = 'Scroll here to open it up · hover to inspect ';
    }
  }

  function start() {
    var anatomy = document.getElementById('anatomy');
    var root = document.documentElement;
    if (!anatomy || !document.getElementById('scene')) return;
    var stage = anatomy.querySelector(STAGE) || anatomy;
    var next = document.getElementById('process') || anatomy.nextElementSibling;
    retitleHint();

    /* ---- 1. lazy-load the heavy scene ---- */
    var injected = false;
    function inject() {
      if (injected || !SELF) return;
      injected = true;
      var s = document.createElement('script');
      /* Keep the theme's asset directory, swap the filename — and carry OUR
         OWN ?v= digest across to it.

         This used to stamp a hand-bumped ?lv= instead, which quietly did
         nothing for a year: Shopify's asset CDN keys on ?v= and strips every
         other query param, so two different lv values returned the same
         cached body (proved 2026-08-12 — a brand-new lv came back with
         age=162632, a two-day-old copy, while a fresh ?v= returned the file
         that had just been pushed). Only the browser cache was ever being
         busted; every visitor with a warm CDN edge kept the old loader.

         asset_url is unavailable inside a .js asset, so the loader's real
         digest can't be read here — but this file's own digest is right there
         in SELF, and it changes whenever this file changes. Editing
         cds-anatomy-3d.js therefore publishes cds-3d-loader.js, which is the
         same rule as before, minus the manual stamp and minus the bug. Touch
         this file (a comment is enough) whenever the loader changes. */
      var VER = (SELF.match(/[?&]v=([^&]*)/) || [])[1] || '';
      s.src = SELF.replace(/cds-anatomy-3d\.js.*$/, 'cds-3d-loader.js') + (VER ? '?v=' + VER : '');
      s.defer = true;
      document.head.appendChild(s);
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries, obs) {
        if (entries.some(function (e) { return e.isIntersecting; })) {
          inject();
          obs.disconnect();
        }
      }, { rootMargin: '150% 0px 150% 0px' }).observe(anatomy);
    } else {
      inject();
    }

    /* ---- 2. the two gates ----
       Measured directly in the scroll handler rather than inside
       requestAnimationFrame. rAF is the usual throttle, but if frames are not
       delivered during a scroll the class never updates and the canvas stays
       gated off while you are looking straight at the section. A few
       getBoundingClientRects per scroll event are cheap (reads, no writes), and
       the early-outs below mean a class is only touched on an actual
       transition.

       These used to be ONE class, and that was the bug Brady hit on
       2026-08-12: "scrolling down to exactly middle of screen it populated and
       then disappears. I need it always present." A single gate had to serve
       two jobs with opposite appetites — whether the converter is SHOWN, and
       whether the wheel over the component list is allowed to scrub it — so it
       was tuned for the stricter one and the converter was only on screen for
       ~600px of a ~2400px section. Split:

       - cds-3d-on    (visibility) — the stage band is anywhere on screen.
       - cds-3d-armed (interaction) — the panel has come to rest.

       Visibility can afford to be generous now that the canvas is bounded:
       cds-3d-loader.js clips it to the same band this measures, so "shown" can
       no longer mean "drawn over the neighbouring section". */
    var head = anatomy.querySelector('.anatomy-nav-head');
    var list = anatomy.querySelector('.anatomy-list');
    var on = null, armed = null;
    function measure() {
      var vh = window.innerHeight || 0;
      if (!vh) return;                      /* 0-height viewport: leave as-is */
      var r = stage.getBoundingClientRect();

      /* ---- shown: the band the model is framed into overlaps the viewport.
         The band, not the pane: the pane is 100svh and can be pinned while its
         content is still half a screen down, and it is the band the converter
         actually lives in. */
      var b0 = r.top, b1 = r.bottom;
      if (head && list) {
        b0 = Math.max(b0, head.getBoundingClientRect().top - 20);
        b1 = Math.min(b1, list.getBoundingClientRect().bottom + 20);
      }
      var want = b1 > 0 && b0 < vh;

      /* ---- armed: unchanged, and deliberately so. Coverage alone earns it
         ~70px early — the stage is 100svh, so it crosses 90% of the viewport
         while still sliding up to its pinned position, and the first wheel
         notch then goes into the explode instead of into finishing the scroll,
         opening the assembly while the list is still moving. The stage is
         sticky top:0, so "settled" has an exact reading: its top edge has
         reached the top of the viewport. Require that too. */
      var covered = Math.min(r.bottom, vh) - Math.max(r.top, 0);
      /* against min(stage, viewport) so a stage shorter than the viewport —
         100svh vs vh on a mobile browser with its bars retracted — can still
         reach the threshold */
      var earned = r.top <= 0 &&
                   covered > 0 && covered >= Math.min(r.height, vh) * COVER;

      /* Held once the stage has reached the top of the viewport and the next
         section still hasn't claimed it. Stated as a position rather than as
         "stay on once you're on", so it reads the same scrolling up as down —
         no direction state, no hysteresis to get out of sync. */
      var held = r.top <= 0 && next &&
                 next.getBoundingClientRect().top > vh * RELEASE;

      var wantArmed = earned || !!held;
      if (want !== on) { on = want; root.classList.toggle('cds-3d-on', want); }
      if (wantArmed !== armed) { armed = wantArmed; root.classList.toggle('cds-3d-armed', wantArmed); }
    }
    addEventListener('scroll', measure, { passive: true });
    addEventListener('resize', measure, { passive: true });
    measure();
    /* re-check once layout settles (fonts, lazy images shifting the section) */
    setTimeout(measure, 600);
    addEventListener('load', measure);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
